/**
 * @file   src\Pages\OrderList.tsx
 * @brief  Order List page.
 * @date   JUL, 2024
 * @authoe ZCO Engineer
 * @copyight (c) 2024, ZCO
 */
import { React,Container, Table, Badge, Breadcrumb, Button, useOutletContext, useNavigate, useEffect, useState,  Link, Pagination } from '../utils/thirdpartyComponents';
import CWSearchinput from '../components/CWSearchinput';
import { AppContextType } from '../interfaces/generalInterface';
import { pageURLs } from '../utils/pageUrls';
import Deviceicon from '../assets/images/computer-chip.svg';
import PaxDeviceIcon from '../assets/images/PAX-device.svg';
import AddIcon from '../assets/images/icons/addicon';
import { DeviceType, getDeviceTypeLabel, userType } from '../utils/enums';
import { AdminOrderHardwareApi, OrderHardwareApi } from '../utils/apiUrls';
import axios from '../config/axiosConfig';
import { formatDate, getBadgeClass, getFromLocalStorage, getOrderStatus, getVisiblePageNumbers } from '../utils/helper';
import SmallLoader from '../components/SmallLoader';

const getOrderDeviceIcon = (deviceType: number) =>
    deviceType === DeviceType.PAX || deviceType === DeviceType.LPR ? PaxDeviceIcon : Deviceicon;

const OrderList = (props: any) => {
    const carwash_owner = (props.userType == userType['CARWASH-OWNER']) ? true : false;
    const { updateData } = useOutletContext<AppContextType>();
    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [searchValue, setSearchValue] = useState<string>('');
    const navigate = useNavigate();
    const storageData = getFromLocalStorage('CARWASH_ADMIN_DATA');
    const userInfo = storageData && storageData !== null ? storageData : null;

    useEffect(() => {
        handleUpdate();
    }, []);

    useEffect(() => {
        getOrderHardwareList()
    }, [currentPage,searchValue]);

    const handleUpdate = () => {
        updateData({
            heading: 'Order List',
            url: `/superadmin/${pageURLs.manage_hardware}`,
            showBackButton: (userInfo?.userTypeId == 2 && userInfo?.permissionType == 2 || props.userType == userType['CARWASH-OWNER'] )  ? false : true,
            showMainMenu: true,
            activeLink: "manage-hardware"
        });
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        // alert("Not implemented");
        const value = event.target.value;
        setSearchValue(value);
        setCurrentPage(1)
        // performSearch(value);
    };



    const handleClearSearch = () => {
        setSearchValue('');
        // Reset orders array if necessary
    };

    const getOrderHardwareList = async () => {
        try {
            setLoading(true);
            // const { data } = await axios.get(`${OrderHardwareApi.ORDER_HARDWARE_LIST}?pageNo=${pageNo}`);
            const { data } = (!carwash_owner) ? await axios.get(`${AdminOrderHardwareApi.ORDER_LIST}?pageNo=${currentPage}&searchText=${searchValue}`) : await axios.get(`${OrderHardwareApi.CARWASH_OWNER_ORDER_LIST}?pageNo=${currentPage}&searchText=${searchValue}`);

            if (data?.error === 0 && Array.isArray(data?.data?.list)) {
                const hardware_list = data.data.list;
                setOrders(hardware_list);
                setTotalPages(Math.ceil(data.data.totalCount / data.data.limit) || 1);
            } else {
                setOrders([]);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };


    return (
        <div className='manage-carwash-container order-list-container'>
            <Container>
                <Breadcrumb>
                    { (userInfo?.userTypeId == 1 || (userInfo?.userTypeId == 2 && userInfo?.permissionType == 1)) &&(
                        <>
                            
                            <Breadcrumb.Item onClick={() => navigate("/superadmin/" + pageURLs.manage_hardware)}>
                                Manage Hardware
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>Order List</Breadcrumb.Item>
                        </>
                    )}
                </Breadcrumb>
                {carwash_owner === true && (
                    <div className="manage-header-block button-group-header d-flex justify-content-end">
                        <Button
                            type="submit"
                            title="Order Hardware"
                            variant="primary"
                            className="primary-btn d-flex align-items-center"
                            onClick={() => navigate("/" + pageURLs.order_hardware, { state: {prevUrl : pageURLs.order_list} })}
                        >
                            Order Hardware
                            <AddIcon />
                        </Button>
                    </div>
                )}
                <div className="manage-header-block d-flex justify-content-between align-items-center">
                    <div className="search-wrap order-search w-100 d-flex">
                        <CWSearchinput
                            id="search"
                            name="search"
                            type="text"
                            placeholder={carwash_owner !== true ? "Search by Order Id, Carwash Name" : "Search by Order Id"}
                            value={searchValue}
                            onChange={handleSearch}
                            onClose={handleClearSearch}
                        />
                    </div>
                </div>
                <div className="cw-table-wrap">
                    <Table responsive className='m-0'>
                        <thead>
                            <tr>
                                <th>&nbsp;</th>
                                <th>Order Number</th>
                                <th>Device Type</th>
                                {carwash_owner !== true && <th>Car Wash Name</th>}
                                <th>Qty</th>
                                <th>Amount</th>
                                {carwash_owner === true && <th>Device Name</th>}
                                {carwash_owner === true && <th>Shipment Date</th>}
                                <th>Order Status</th>
                                {carwash_owner === true && <th>Tracking Number</th>}
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.length > 0 ? orders.map((order: any, index) => (
                                <tr key={index}>
                                    <td width='6%'>
                                        <span className="d-flex frame">
                                            <img src={getOrderDeviceIcon(order.DeviceType)} alt={getDeviceTypeLabel(order.DeviceType)} />
                                        </span>
                                    </td>
                                    <td width='12%' className='f-semi'>{order.OrderID}</td>
                                    <td width='12%' className='f-semi'>{getDeviceTypeLabel(order.DeviceType)}</td>
                                    {carwash_owner !== true && <td width='18%'>{order.CompanyName}</td>}
                                    <td width='6%'>{order.Quanity}</td>
                                    <td>{`$${order.TotalAmount}`}</td>
                                    {carwash_owner === true && <td>{order.DeviceName}</td>}
                                    {carwash_owner === true && <td>{order?.ShippedDate? formatDate(order?.ShippedDate?.toString())  : 'Pending'}</td>}
                                    <td width='18%'>
                                       {order.OrderStatus!=null && <Badge className={getBadgeClass(order.OrderStatus)}>{getOrderStatus(order.OrderStatus)}</Badge>}
                                    </td>
                                    {carwash_owner === true && (
                                        <td>
                                            {order.OrderStatus === 2 ? (
                                                <Link to={`/${pageURLs.track_order}`}>{order.TrackingNumber || 'N/A'}</Link>
                                            ) : 'N/A'}
                                        </td>
                                    )}
                                    <td className='nowrap'>
                                        <Button
                                            title="View Details"
                                            variant="outline-primary"
                                            className="outline-btn"
                                            onClick={() => navigate(`/${carwash_owner ? pageURLs.order_details : 'superadmin/' + pageURLs.order_details}`,{state:{orderID:order.CarWashPurchaseDtlsID}})}
                                        >
                                            View Details
                                        </Button>
                                    </td>
                                </tr>
                            )) :
                            <tr>
                                        <td style={{ textAlign: 'center' }} colSpan={7} >No data found.</td>
                                    </tr>
                            }
                        </tbody>
                    </Table>
                    {orders.length ? <div className="mt-3 d-flex justify-content-end">
                        <Pagination>
                            <Pagination.First
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(1)}
                            />
                            <Pagination.Prev
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            />
                            {getVisiblePageNumbers(currentPage,totalPages).map((pageNumber) => (
                                <Pagination.Item
                                    key={pageNumber}
                                    active={pageNumber === currentPage}
                                    onClick={() => handlePageChange(pageNumber)}
                                >
                                    {pageNumber}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            />
                            <Pagination.Last
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(totalPages)}
                            />
                        </Pagination>
                    </div> : null}
                </div>
            </Container>
            {loading && <SmallLoader />}
        </div>
    );
};

export default OrderList;
